import { Component } from 'react';
import * as React from 'react';
import { TBreakpoint as Breakpoint } from '../App';
import { Button } from 'reactstrap';
import { copy } from '../ClipboardUtils';
import SyntaxHighlighter from "react-syntax-highlighter";

interface Props {
    breakpoints: Breakpoint[];
    responsive: boolean;
    backgroundImage: boolean;
    onSnippetChange?: (snippet: string) => void;
    altText?: string;
    lazy: boolean;
    apiKey: string;
}

interface Op {
    op: string;
    params?: string;
}

class HTMLSnippet extends Component<Props, {}> {

    static op(b: Breakpoint): Op {
        if (b.hide) {
            return {op: 'hide'};
        } else if (b.asis) {
            return {op: 'asis'};
        } else if (b.width && b.height) {
            return {op: 'fit', params: `size=${b.width}x${b.height}`};
        } else if (b.width || b.height) {
            return {op: 'resize', params: `size=${b.width ? b.width.toString(10) : 'x' + b.height}`};
        }

        return {op: 'optimise'};
    }

    generateSnippet() {
        let snippet: string;

        if (this.props.responsive) {
            snippet = this.generateResponsiveSnippet();
        } else {
            snippet = this.generateImgSnippet();
        }
        if (this.props.onSnippetChange) {
            this.props.onSnippetChange(snippet);
        }
        return snippet;
    }

    generatePictureChild(b: Breakpoint, alt: string | undefined, isLazy: boolean): string {
        const src = this.src(b.url, HTMLSnippet.op(b));
        if (b.mediaQuery || isLazy) {
            return `<source ${b.mediaQuery ? `media="${b.mediaQuery}"` : ''} srcset="${src}"/>`;
        }

        return `<img src="${src}" ${alt ? `alt="${alt}"` : ''}/>`;
    }

    generateResponsiveSnippet(): string {
        const isLazy = this.props.lazy;
        const hasAltText = this.props.altText;

        let snippet =
            `<picture`;
        if (isLazy) {
            snippet += ' data-lazy=""';
        }
        if (hasAltText && isLazy) {
            snippet += ` data-alt="${this.props.altText}"`;
        }
        snippet += '>';
        for (let i = 0; i < this.props.breakpoints.length; i++) {
            snippet += `\n    ${this.generatePictureChild(this.props.breakpoints[i], this.props.altText, isLazy)}`;
        }
        snippet += '\n</picture>';
        return snippet;
    }

    src(imageUrl: string, op: Op): string {
        if (op.op === 'hide') {
            return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
        }

        const escapedImageUrl = imageUrl.replace(/\?/, '%3F');
        let pixboostUrl = `https://pixboost.com/api/2/img/${escapedImageUrl}/${op.op}?`;
        if (op.params) {
            pixboostUrl += `${op.params}&`;
        }
        pixboostUrl += `auth=${this.props.apiKey}`;
        return pixboostUrl;
    }

    generateImgSnippet(): string {
        const bp = this.props.breakpoints[0];
        if (!bp) {
            return '';
        }

        const op = HTMLSnippet.op(bp);
        const isLazy = this.props.lazy;
        const hasAltText = this.props.altText;
        const src = this.src(bp.url, op);

        let snippet = '<img';
        if (hasAltText) {
            snippet += ` alt="${this.props.altText}"`;
        }
        if (isLazy) {
            snippet += ' data-lazy=""';
            snippet += `\n    data-src="${src}"`;
        } else {
            snippet += `\n    src="${src}"`;
        }

        snippet += '/>';
        return snippet;
    }

    render() {
        return (
            <div>
                <div style={{backgroundColor: 'lightyellow', padding: 10}}>
                    <SyntaxHighlighter language="html" customStyle={{backgroundColor: 'lightyellow'}}>
                        {this.generateSnippet()}
                    </SyntaxHighlighter>
                    <div style={{width: '100%', textAlign: 'right'}}>
                        <div className={'pb-2'}>
                            <Button color={'primary'} onClick={() => { copy(this.generateSnippet()); }}>Copy</Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default HTMLSnippet;